import { Theme } from "@mui/material/styles";

import { colors } from "@/theme";
import { MarketOdds, SportEvent } from "@/types";

import { OddDirection } from "../types";
import { OddsFormat, formatOddsValue } from "./oddsFormat";

export const DEFAULT_EXPANDED_COUNT = 2;

export const EVENT_STATUS_REFETCH_INTERVAL: Record<"live" | "next24" | "upcoming", number> = {
  live: 10_000,
  next24: 60_000,
  upcoming: 300_000,
};

export const getEventStatusRefetchInterval = (eventStatus: string): number | false =>
  EVENT_STATUS_REFETCH_INTERVAL[eventStatus as keyof typeof EVENT_STATUS_REFETCH_INTERVAL] ?? false;

const FLICK_MS = 2200;

export const MARKET_DEFS: { name: string; title: string }[] = [
  { name: "ML", title: "1 X 2" },
  { name: "Spread", title: "HCP" },
  { name: "Totals", title: "TOT" },
];

export const isDarkTheme = (theme: Theme): boolean => theme.palette.mode === "dark";
export const accentColor = (theme: Theme): string =>
  isDarkTheme(theme) ? colors.prime : colors.schnitzel;
export const borderColor = (theme: Theme): string =>
  isDarkTheme(theme) ? colors.borderDark : colors.border;
export const headerBg = (theme: Theme): string =>
  isDarkTheme(theme) ? colors.bgDark : colors.bgLight;
export const surfaceBg = (theme: Theme): string =>
  isDarkTheme(theme) ? colors.layer1Dark : colors.white;

const OUTCOME_LABEL: Record<string, string> = { home: "1", draw: "X", away: "2" };

export const isLockedPrice = (price?: string): boolean => !price || price === "N/A";

export const getMainOdds = (event: SportEvent, marketName: string): MarketOdds | undefined => {
  const market = event.markets?.find((m) => m.name === marketName);
  if (!market?.odds?.length) return undefined;
  // The feed can repeat a line; count each once so the middle one is still the main line.
  const seen = new Set<string>();
  const lines = market.odds.filter((row) => {
    const key = `${row.hdp ?? ""}__${row.label ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return lines[Math.floor(lines.length / 2)];
};

const signed = (value: number): string => (value > 0 ? `+${value}` : `${value}`);

export const cellLabel = (marketName: string, key: string, hdp?: number): string => {
  if (marketName === "ML") return OUTCOME_LABEL[key] ?? key.toUpperCase();
  const line = hdp ?? 0;
  if (marketName === "Totals") return `${key === "over" ? "O" : "U"} ${line}`;
  const isHomeSide = key === "home" || key === "over";
  return signed(isHomeSide ? line : -line);
};

/** A price as the player reads it: decimal by default, or their chosen format. */
export const formatPrice = (price?: string | number, format: OddsFormat = "decimal"): string => {
  const value = typeof price === "number" ? price : parseFloat(price ?? "");
  return Number.isFinite(value) ? formatOddsValue(value, format) : "";
};

export const flickerAnimationSx = (theme: Theme, direction: OddDirection) => {
  const accent = accentColor(theme);
  const flick = direction === "down" ? colors.red : accent;
  const animName = `oddFlick-${direction}`;
  const original = `var(--odds-ink, ${theme.palette.text.primary})`;

  return {
    animation: `${animName} ${FLICK_MS}ms steps(1, end) 1`,
    [`@keyframes ${animName}`]:
      direction === "up"
        ? {
            "0%": { color: accent },
            "20%": { color: original },
            "40%": { color: accent },
            "60%": { color: original },
            "80%": { color: accent },
            "100%": { color: original },
          }
        : {
            "0%": { color: flick },
            "20%": { color: original },
            "40%": { color: flick },
            "60%": { color: original },
            "80%": { color: flick },
            "100%": { color: original },
          },
  };
};

export const teamInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

export const leagueOnly = (event: SportEvent): string => {
  const league = event.league?.name ?? "";
  const prefix = `${event.region?.name ?? ""} - `;
  return league.startsWith(prefix) ? league.slice(prefix.length) : league;
};

export const bannerBackgroundSx = (backgroundUrl: string) => ({
  backgroundImage: `url(${backgroundUrl})`,
  backgroundSize: "cover" as const,
  backgroundPosition: "center" as const,
});
